// Feature: MCRO001-F0026
// Test: T-MCRO-026

export class DemonstrationTimingMapError extends Error {
  constructor(code, message) {
    super(message);
    this.name = 'DemonstrationTimingMapError';
    this.code = code;
  }
}

const fail = (code, message) => { throw new DemonstrationTimingMapError(code, message); };
const integer = (value) => typeof value === 'number' && Number.isFinite(value) && Number.isInteger(value) ? value : null;
const finiteNumber = (value) => typeof value === 'number' && Number.isFinite(value) ? value : null;
const identifier = (value) => typeof value === 'string' ? value.trim() : '';
const pointKey = ({ kind, sectionId, count }) => `${kind}:${sectionId}:${count}`;

function selectedSections(sections, startCount, endCount) {
  if (!Array.isArray(sections) || sections.length === 0) {
    fail('SECTION_COVERAGE', 'The selected revision does not contain the demonstration sections.');
  }
  const normalized = sections.map((section) => ({
    sectionId: identifier(section?.sectionId) || identifier(section?.id),
    label: identifier(section?.label) || identifier(section?.title) || 'Untitled section',
    startCount: integer(section?.startCount),
    endCount: integer(section?.endCount),
  }));
  if (normalized.some(({ sectionId, startCount: start, endCount: end }) => !sectionId || start === null || end === null || start < 1 || end < start)) {
    fail('SECTION_INVALID', 'Every authored section requires an identity and valid whole-count bounds.');
  }

  const overlapping = normalized
    .filter(({ startCount: start, endCount: end }) => end >= startCount && start <= endCount)
    .sort((left, right) => left.startCount - right.startCount);
  if (overlapping.length === 0) {
    fail('SECTION_COVERAGE', 'The selected revision does not contain the complete demonstration range.');
  }
  const ids = new Set();
  const starts = new Set();
  overlapping.forEach((section, index) => {
    if (ids.has(section.sectionId) || starts.has(section.startCount)) {
      fail('SECTION_DUPLICATE', 'Section identities and boundaries must be unique.');
    }
    ids.add(section.sectionId);
    starts.add(section.startCount);
    if (index > 0 && section.startCount !== overlapping[index - 1].endCount + 1) {
      fail('SECTION_COVERAGE', 'Authored sections must cover the range without gaps or overlap.');
    }
  });
  if (overlapping[0].startCount > startCount || overlapping.at(-1).endCount < endCount) {
    fail('SECTION_COVERAGE', 'The selected revision does not contain the complete demonstration range.');
  }
  return overlapping;
}

function selectedAuthoredBars(authoredBars, revisionId, sections, startCount, endCount) {
  if (!Array.isArray(authoredBars) || authoredBars.length === 0) {
    fail('BAR_BOUNDARY_INCOMPLETE', 'The Rehearsal selector must supply its complete authored bar model.');
  }
  const bars = authoredBars.map((bar) => ({
    barId: identifier(bar?.barId) || identifier(bar?.id),
    revisionId: identifier(bar?.revisionId),
    sectionId: identifier(bar?.sectionId),
    startCount: integer(bar?.startCount),
    endCount: integer(bar?.endCount),
  }));
  if (bars.some(({ barId, revisionId: barRevisionId, sectionId }) => !barId || !barRevisionId || !sectionId)) {
    fail('BAR_BOUNDARY_IDENTITY', 'Every authored bar requires revision-bound bar and section identities.');
  }
  if (bars.some((bar) => bar.revisionId !== revisionId)) {
    fail('BAR_BOUNDARY_STALE', 'Every authored bar must belong to the selected Sheet revision.');
  }
  if (bars.some(({ startCount: start, endCount: end }) => start === null || end === null || start < 1 || end < start)) {
    fail('BAR_BOUNDARY_INCOMPLETE', 'Every authored selector boundary requires valid whole-count bounds.');
  }
  const barIds = bars.map(({ barId }) => barId);
  const boundaryIds = bars.map(({ sectionId, startCount: start, endCount: end }) => `${sectionId}:${start}:${end}`);
  if (new Set(barIds).size !== barIds.length || new Set(boundaryIds).size !== boundaryIds.length) {
    fail('BAR_BOUNDARY_DUPLICATE', 'Authored bar identities and boundaries must be unique.');
  }
  bars.forEach((bar, index) => {
    if (index > 0 && bar.startCount <= bars[index - 1].startCount) {
      fail('BAR_BOUNDARY_INCOMPLETE', 'Authored bars must be supplied in selector order.');
    }
  });

  const startIndex = bars.findIndex((bar) => bar.startCount === startCount);
  const endIndex = bars.findIndex((bar) => bar.endCount === endCount);
  if (startIndex < 0 || endIndex < startIndex) {
    fail('INVALID_RANGE', 'Choose exact ordered endpoints from the authored Rehearsal bar selector.');
  }
  const selected = bars.slice(startIndex, endIndex + 1);
  if (selected.some(({ startCount: start, endCount: end }) => end - start !== 7)) {
    fail('BAR_BOUNDARY_INCOMPLETE', 'Every selected demonstration bar must contain exactly eight whole counts.');
  }
  if (selected.some((bar, index) => index > 0 && bar.startCount !== selected[index - 1].endCount + 1)) {
    fail('BAR_BOUNDARY_INCOMPLETE', 'The selected authored bars must cover the range without gaps.');
  }
  selected.forEach((bar) => {
    const section = sections.find(({ sectionId }) => sectionId === bar.sectionId);
    if (!section || bar.startCount < section.startCount || bar.endCount > section.endCount) {
      fail('BAR_BOUNDARY_IDENTITY', 'Every selected authored bar must remain inside its identified section.');
    }
  });
  return selected;
}

function expectedPoints(revisionId, sections, startCount, endCount, mediaStartMs, mediaEndMs) {
  const startSection = sections.find((section) => startCount >= section.startCount && startCount <= section.endCount);
  const endSection = sections.find((section) => endCount >= section.startCount && endCount <= section.endCount);
  if (!startSection || !endSection) fail('SECTION_COVERAGE', 'The range endpoints must belong to authored sections.');
  const internal = sections.filter(({ startCount: boundary }) => boundary > startCount && boundary < endCount);
  const countSpan = endCount - startCount;
  const mediaSpan = mediaEndMs - mediaStartMs;
  const point = (kind, section, count, mediaMs) => ({
    syncPointId: `${kind}:${section.sectionId}:${count}`,
    kind,
    revisionId,
    sectionId: section.sectionId,
    sectionLabel: section.label,
    count,
    mediaMs,
  });
  return [
    point('start', startSection, startCount, mediaStartMs),
    ...internal.map((section) => point(
      'section_boundary',
      section,
      section.startCount,
      Math.round(mediaStartMs + ((section.startCount - startCount) / countSpan) * mediaSpan),
    )),
    point('end', endSection, endCount, mediaEndMs),
  ];
}

function prepareTimingMap(value, expectedRevisionId) {
  const trustedRevisionId = identifier(expectedRevisionId);
  const revisionId = identifier(value?.revisionId);
  const renditionId = identifier(value?.renditionId);
  const durationMs = integer(value?.durationMs);
  const mediaStartMs = integer(value?.mediaStartMs);
  const mediaEndMs = integer(value?.mediaEndMs);
  const startCount = integer(value?.startCount);
  const endCount = integer(value?.endCount);
  if (!trustedRevisionId) fail('EXPECTED_REVISION_MISSING', 'The current trusted Sheet revision is required.');
  if (!revisionId) fail('REVISION_MISSING', 'A Sheet revision is required.');
  if (revisionId !== trustedRevisionId) fail('REVISION_STALE', 'The timing map does not belong to the current Sheet revision.');
  if (!renditionId || durationMs === null || durationMs <= 0) fail('RENDITION_UNAVAILABLE', 'An approved rendition with duration is required.');
  if (startCount === null || endCount === null || startCount < 1 || endCount <= startCount) {
    fail('INVALID_RANGE', 'Choose an ordered range of complete authored eight-count bars.');
  }
  if (mediaStartMs === null || mediaEndMs === null || mediaStartMs < 0
      || mediaEndMs <= mediaStartMs || mediaEndMs > durationMs) {
    fail('INVALID_MEDIA_ENDPOINTS', 'Video endpoints must be ordered inside the approved rendition.');
  }
  const sections = selectedSections(value.sections, startCount, endCount);
  const authoredBars = selectedAuthoredBars(value.authoredBars, revisionId, sections, startCount, endCount);
  const expected = expectedPoints(revisionId, sections, startCount, endCount, mediaStartMs, mediaEndMs);
  return {
    revisionId,
    renditionId,
    durationMs,
    mediaStartMs,
    mediaEndMs,
    startCount,
    endCount,
    sections,
    authoredBars,
    expected,
  };
}

export function validateDemonstrationTimingMap(value, expectedRevisionId) {
  const prepared = prepareTimingMap(value, expectedRevisionId);
  if (!Array.isArray(value?.syncPoints)) {
    fail('SYNC_POINTS_REQUIRED', 'A persisted timing map requires an explicit sync-point array.');
  }
  const supplied = value.syncPoints.map((item) => ({
    syncPointId: identifier(item?.syncPointId),
    kind: identifier(item?.kind),
    revisionId: identifier(item?.revisionId),
    sectionId: identifier(item?.sectionId),
    sectionLabel: identifier(item?.sectionLabel) || prepared.sections.find(({ sectionId }) => sectionId === item?.sectionId)?.label || '',
    count: integer(item?.count),
    mediaMs: integer(item?.mediaMs),
  }));

  if (supplied.some((point) => point.revisionId !== prepared.revisionId)) {
    fail('SYNC_POINT_STALE', 'Every sync point must belong to the selected Sheet revision.');
  }
  if (supplied.some(({ syncPointId }) => !syncPointId)) {
    fail('SYNC_POINT_IDENTITY', 'Every sync point requires a stable identity.');
  }
  const suppliedIds = supplied.map(({ syncPointId }) => syncPointId);
  if (new Set(suppliedIds).size !== suppliedIds.length) fail('SYNC_POINT_DUPLICATE', 'Sync point identities must be unique.');
  const suppliedKeys = supplied.map(pointKey);
  if (new Set(suppliedKeys).size !== suppliedKeys.length) fail('SYNC_POINT_DUPLICATE', 'Sync point count identities must be unique.');
  const expectedKeys = prepared.expected.map(pointKey);
  if (supplied.length < prepared.expected.length) fail('SYNC_POINT_MISSING', 'Every internal authored section boundary requires one sync point.');
  if (supplied.length === prepared.expected.length && suppliedKeys.some((key) => !expectedKeys.includes(key))) {
    fail('SYNC_POINT_IDENTITY', 'Sync points cannot change section or count identity.');
  }
  if (expectedKeys.some((key) => !suppliedKeys.includes(key))) fail('SYNC_POINT_MISSING', 'Every internal authored section boundary requires one sync point.');
  if (suppliedKeys.some((key) => !expectedKeys.includes(key))) fail('SYNC_POINT_IDENTITY', 'Sync points cannot change section or count identity.');
  if (suppliedKeys.some((key, index) => key !== expectedKeys[index])) fail('SYNC_POINT_ORDER', 'Sync points must remain in authored count order.');
  supplied.forEach((point, index) => {
    const expected = prepared.expected[index];
    if (point.syncPointId !== expected.syncPointId || point.count !== expected.count
        || point.sectionId !== expected.sectionId || point.kind !== expected.kind) {
      fail('SYNC_POINT_IDENTITY', 'Sync points cannot change authored identity.');
    }
    if (point.mediaMs === null || point.mediaMs < prepared.mediaStartMs || point.mediaMs > prepared.mediaEndMs) {
      fail('SYNC_POINT_OUTSIDE', 'Sync point video times must stay inside the selected segment.');
    }
    if (index > 0 && point.mediaMs <= supplied[index - 1].mediaMs) {
      fail('SYNC_POINT_CROSSED', 'Sync point video times must be strictly increasing.');
    }
  });
  if (supplied[0]?.mediaMs !== prepared.mediaStartMs || supplied.at(-1)?.mediaMs !== prepared.mediaEndMs) {
    fail('INVALID_MEDIA_ENDPOINTS', 'Start and end sync points must match the selected video endpoints.');
  }

  const { expected, ...map } = prepared;
  return { ...map, syncPoints: supplied };
}

export function createProportionalTimingMap(input, expectedRevisionId) {
  const prepared = prepareTimingMap(input, expectedRevisionId);
  return validateDemonstrationTimingMap({ ...input, syncPoints: prepared.expected }, expectedRevisionId);
}

export function moveTimingSyncPoint(map, syncPointId, mediaMs, expectedRevisionId) {
  const current = validateDemonstrationTimingMap(map, expectedRevisionId);
  const index = current.syncPoints.findIndex((point) => point.syncPointId === syncPointId);
  if (index < 0) fail('SYNC_POINT_MISSING', 'The selected sync point is unavailable.');
  const nextMediaMs = integer(mediaMs);
  if (nextMediaMs === null) fail('SYNC_POINT_OUTSIDE', 'Sync point video time must be a whole millisecond.');
  const selected = current.syncPoints[index];
  const next = {
    ...current,
    mediaStartMs: selected.kind === 'start' ? nextMediaMs : current.mediaStartMs,
    mediaEndMs: selected.kind === 'end' ? nextMediaMs : current.mediaEndMs,
    syncPoints: current.syncPoints.map((point, pointIndex) => pointIndex === index ? { ...point, mediaMs: nextMediaMs } : { ...point }),
  };
  return validateDemonstrationTimingMap(next, expectedRevisionId);
}

export function stepTimingSyncPoint(map, syncPointId, direction, frameStepMs = 40, expectedRevisionId) {
  const current = validateDemonstrationTimingMap(map, expectedRevisionId);
  const selected = current.syncPoints.find((point) => point.syncPointId === syncPointId);
  if (!selected) fail('SYNC_POINT_MISSING', 'The selected sync point is unavailable.');
  const step = integer(frameStepMs);
  if (step === null || step < 1 || ![-1, 1].includes(direction)) fail('FRAME_STEP_INVALID', 'Frame movement requires one positive step and direction.');
  return moveTimingSyncPoint(current, syncPointId, selected.mediaMs + (direction * step), expectedRevisionId);
}

export function mediaMsFromScrubberPointer({ clientX, left, width, mediaStartMs, mediaEndMs } = {}) {
  const x = finiteNumber(clientX);
  const origin = finiteNumber(left);
  const span = finiteNumber(width);
  const start = integer(mediaStartMs);
  const end = integer(mediaEndMs);
  if (x === null || origin === null || span === null || span <= 0 || start === null || end === null || end <= start) {
    fail('SCRUBBER_GEOMETRY_INVALID', 'The scrubber geometry and endpoints are unavailable.');
  }
  const ratio = Math.min(1, Math.max(0, (x - origin) / span));
  return Math.round(start + ratio * (end - start));
}

export function projectCountToMediaMs(map, count, expectedRevisionId) {
  const current = validateDemonstrationTimingMap(map, expectedRevisionId);
  const target = finiteNumber(count);
  if (target === null || target < current.startCount || target > current.endCount) fail('COUNT_OUTSIDE', 'Count is outside the demonstration range.');
  const exact = current.syncPoints.find((point) => point.count === target);
  if (exact) return exact.mediaMs;
  const rightIndex = current.syncPoints.findIndex((point) => point.count > target);
  const left = current.syncPoints[rightIndex - 1];
  const right = current.syncPoints[rightIndex];
  return Math.round(left.mediaMs + ((target - left.count) / (right.count - left.count)) * (right.mediaMs - left.mediaMs));
}

export function projectMediaMsToCount(map, mediaMs, expectedRevisionId) {
  const current = validateDemonstrationTimingMap(map, expectedRevisionId);
  const target = finiteNumber(mediaMs);
  if (target === null || target < current.mediaStartMs || target > current.mediaEndMs) fail('MEDIA_OUTSIDE', 'Video time is outside the demonstration segment.');
  const exact = current.syncPoints.find((point) => point.mediaMs === target);
  if (exact) return exact.count;
  const rightIndex = current.syncPoints.findIndex((point) => point.mediaMs > target);
  const left = current.syncPoints[rightIndex - 1];
  const right = current.syncPoints[rightIndex];
  return left.count + ((target - left.mediaMs) / (right.mediaMs - left.mediaMs)) * (right.count - left.count);
}
